import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import ResponsiveTableWrapper from '../components/ResponsiveTableWrapper';
import { churchDirectoryService, ChurchDirectory, ChurchDirectoryFilters, CreateChurchData, UpdateChurchData } from '../api/churchDirectoryService';
import { planterService } from '../api/planterService';

const ChurchDirectoryPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState('');
  const [denominationFilter, setDenominationFilter] = useState('');
  const [relationshipFilter, setRelationshipFilter] = useState('');
  const [selectedChurch, setSelectedChurch] = useState<ChurchDirectory | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [churchToDelete, setChurchToDelete] = useState<ChurchDirectory | null>(null);

  const [formData, setFormData] = useState<CreateChurchData>({
    churchName: '',
    contactName: '',
    contactPosition: '',
    contactPhone: '',
    contactEmail: '',
    city: '',
    state: '',
    addressStreet: '',
    addressStreet2: '',
    addressCity: '',
    addressState: '',
    addressZip: '',
    addressCountry: 'United States',
    churchPlanterId: undefined,
    relationshipType: undefined,
    relationshipNotes: '',
    denomination: '',
    website: '',
    notes: '',
  });

  const filters: ChurchDirectoryFilters = {
    page,
    limit,
    search: search || undefined,
    denomination: denominationFilter || undefined,
    relationshipType: relationshipFilter || undefined,
  };

  // Fetch churches
  const { data: churchesData, isLoading } = useQuery({
    queryKey: ['churches', filters],
    queryFn: () => churchDirectoryService.getAllChurches(filters),
  });

  // Fetch planters for dropdown
  const { data: plantersData } = useQuery({
    queryKey: ['planters'],
    queryFn: () => planterService.getAllPlanters({ limit: 1000 }),
  });

  const churches: ChurchDirectory[] = churchesData?.data || [];
  const pagination = churchesData?.pagination;
  const planters = plantersData?.data || [];

  // Create church mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateChurchData) => churchDirectoryService.createChurch(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['churches'] });
      setShowModal(false);
      resetForm();
    },
  });

  // Update church mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateChurchData }) =>
      churchDirectoryService.updateChurch(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['churches'] });
      setShowModal(false);
      setSelectedChurch(null);
      resetForm();
    },
  });

  // Delete church mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => churchDirectoryService.deleteChurch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['churches'] });
      setShowDeleteConfirm(false);
      setChurchToDelete(null);
    },
  });

  const resetForm = () => {
    setFormData({
      churchName: '',
      contactName: '',
      contactPosition: '',
      contactPhone: '',
      contactEmail: '',
      city: '',
      state: '',
      addressStreet: '',
      addressStreet2: '',
      addressCity: '',
      addressState: '',
      addressZip: '',
      addressCountry: 'United States',
      churchPlanterId: undefined,
      relationshipType: undefined,
      relationshipNotes: '',
      denomination: '',
      website: '',
      notes: '',
    });
  };

  const handleEdit = (church: ChurchDirectory) => {
    setSelectedChurch(church);
    setFormData({
      churchName: church.churchName,
      contactName: church.contactName || '',
      contactPosition: church.contactPosition || '',
      contactPhone: church.contactPhone || '',
      contactEmail: church.contactEmail || '',
      city: church.city || '',
      state: church.state || '',
      addressStreet: church.addressStreet || '',
      addressStreet2: church.addressStreet2 || '',
      addressCity: church.addressCity || '',
      addressState: church.addressState || '',
      addressZip: church.addressZip || '',
      addressCountry: church.addressCountry || 'United States',
      churchPlanterId: church.churchPlanterId,
      relationshipType: church.relationshipType,
      relationshipNotes: church.relationshipNotes || '',
      denomination: church.denomination || '',
      website: church.website || '',
      notes: church.notes || '',
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setSelectedChurch(null);
    resetForm();
    setShowModal(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedChurch) {
      updateMutation.mutate({ id: selectedChurch.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDeleteClick = (church: ChurchDirectory) => {
    setChurchToDelete(church);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    if (churchToDelete) {
      deleteMutation.mutate(churchToDelete.id);
    }
  };

  const uniqueDenominations = Array.from(new Set(churches.map(c => c.denomination).filter(Boolean)));

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Church Directory</h1>
              <p className="mt-2 text-sm text-gray-600">
                Manage church directory, partnerships, and planter relationships
              </p>
            </div>
            <button
              onClick={handleAdd}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Church
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <input
                  type="text"
                  placeholder="Search churches..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
              <div>
                <select
                  value={denominationFilter}
                  onChange={(e) => setDenominationFilter(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All Denominations</option>
                  {uniqueDenominations.map((denom) => (
                    <option key={denom} value={denom}>{denom}</option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  value={relationshipFilter}
                  onChange={(e) => setRelationshipFilter(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All Relationship Types</option>
                  <option value="sending">Sending</option>
                  <option value="supporting">Supporting</option>
                  <option value="partnering">Partnering</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Churches Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="px-4 py-12 text-center text-gray-500">Loading churches...</div>
          ) : churches.length === 0 ? (
            <div className="px-4 py-12 text-center text-gray-500">
              No churches found. Click "Add Church" to create one.
            </div>
          ) : (
            <ResponsiveTableWrapper>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Church Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Contact
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Related Planter
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Relationship
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sm:px-6">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {churches.map((church) => (
                  <tr key={church.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-normal sm:px-6 sm:whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{church.churchName}</div>
                      {church.denomination && (
                        <div className="text-sm text-gray-500">{church.denomination}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-normal sm:px-6 sm:whitespace-nowrap">
                      {church.contactName && (
                        <div className="text-sm text-gray-900">
                          {church.contactName}
                          {church.contactPosition && ` (${church.contactPosition})`}
                        </div>
                      )}
                      <div className="text-sm text-gray-500">{church.contactEmail}</div>
                      <div className="text-sm text-gray-500">{church.contactPhone}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-normal sm:px-6 sm:whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {church.city && church.state ? `${church.city}, ${church.state}` : church.city || church.state || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-normal sm:px-6 sm:whitespace-nowrap">
                      {church.churchPlanter ? (
                        <div className="text-sm text-gray-900">{church.churchPlanter.name}</div>
                      ) : (
                        <div className="text-sm text-gray-500">-</div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap sm:px-6">
                      {church.relationshipType && (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          {church.relationshipType}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium sm:px-6">
                      <div className="flex flex-col items-end gap-2 sm:flex-row sm:justify-end sm:items-center">
                        <button
                          onClick={() => handleEdit(church)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(church)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </ResponsiveTableWrapper>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{page}</span> of{' '}
                  <span className="font-medium">{pagination.pages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowModal(false)} />
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
                <form onSubmit={handleSubmit}>
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 max-h-[80vh] overflow-y-auto">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {selectedChurch ? 'Edit Church' : 'Add Church'}
                    </h3>
                    <div className="space-y-6">
                      {/* Basic Information */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Basic Information</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Church Name *</label>
                            <input
                              type="text"
                              required
                              value={formData.churchName}
                              onChange={(e) => setFormData({ ...formData, churchName: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Denomination</label>
                            <input
                              type="text"
                              value={formData.denomination}
                              onChange={(e) => setFormData({ ...formData, denomination: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Website</label>
                            <input
                              type="url"
                              value={formData.website}
                              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Contact Information */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Contact Information</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Contact Name</label>
                            <input
                              type="text"
                              value={formData.contactName}
                              onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Contact Position</label>
                            <input
                              type="text"
                              value={formData.contactPosition}
                              onChange={(e) => setFormData({ ...formData, contactPosition: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Contact Email</label>
                            <input
                              type="email"
                              value={formData.contactEmail}
                              onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
                            <input
                              type="tel"
                              value={formData.contactPhone}
                              onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Location */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Location</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">City</label>
                            <input
                              type="text"
                              value={formData.city}
                              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">State</label>
                            <input
                              type="text"
                              value={formData.state}
                              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Physical Address */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Physical Address</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Street Address</label>
                            <input
                              type="text"
                              value={formData.addressStreet}
                              onChange={(e) => setFormData({ ...formData, addressStreet: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Street Address 2</label>
                            <input
                              type="text"
                              value={formData.addressStreet2}
                              onChange={(e) => setFormData({ ...formData, addressStreet2: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">City</label>
                            <input
                              type="text"
                              value={formData.addressCity}
                              onChange={(e) => setFormData({ ...formData, addressCity: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">State</label>
                            <input
                              type="text"
                              value={formData.addressState}
                              onChange={(e) => setFormData({ ...formData, addressState: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">ZIP Code</label>
                            <input
                              type="text"
                              value={formData.addressZip}
                              onChange={(e) => setFormData({ ...formData, addressZip: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Country</label>
                            <input
                              type="text"
                              value={formData.addressCountry}
                              onChange={(e) => setFormData({ ...formData, addressCountry: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Relationship */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Church Planter Relationship (Optional)</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Related Church Planter</label>
                            <select
                              value={formData.churchPlanterId || ''}
                              onChange={(e) => setFormData({ ...formData, churchPlanterId: e.target.value ? parseInt(e.target.value) : undefined })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            >
                              <option value="">None</option>
                              {planters.map((planter: any) => (
                                <option key={planter.id} value={planter.id}>
                                  {planter.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Relationship Type</label>
                            <select
                              value={formData.relationshipType || ''}
                              onChange={(e) => setFormData({ ...formData, relationshipType: e.target.value as any })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            >
                              <option value="">Select type...</option>
                              <option value="sending">Sending</option>
                              <option value="supporting">Supporting</option>
                              <option value="partnering">Partnering</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Relationship Notes</label>
                            <textarea
                              rows={2}
                              value={formData.relationshipNotes}
                              onChange={(e) => setFormData({ ...formData, relationshipNotes: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Notes */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">General Notes</label>
                        <textarea
                          rows={3}
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      disabled={createMutation.isPending || updateMutation.isPending}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setSelectedChurch(null);
                        resetForm();
                      }}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && churchToDelete && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDeleteConfirm(false)} />
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Church</h3>
                  <p className="text-sm text-gray-500">
                    Are you sure you want to delete <strong>{churchToDelete.churchName}</strong>? This action cannot be undone.
                  </p>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    onClick={handleDeleteConfirm}
                    disabled={deleteMutation.isPending}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                  </button>
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setChurchToDelete(null);
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ChurchDirectoryPage;

